import {css, CSSResult, html, LitElement, nothing, PropertyValues, unsafeCSS,} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {consume} from '@lit/context';
import typographyStyles from '../../../styles/responsive-nui-typography.scss';
import styles from './share-collection-layer.scss';
import closeIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-16_close.svg';
import userAddIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-24_user-add.svg';
import sharedIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-24_eye.svg';
import privateIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-24_eye-closed.svg';
import copyIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-24_copy.svg';
import checkmarkIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-16_check.svg';
import {CollectionTrackingContext, Messages, WishlistCollection} from '../../../@types/wishlist.js';
import {i18nContext} from '../../../scripts/context/i18nContext.js';
import {WishlistAppConfig, wishlistAppConfigContext} from '../../../scripts/context/appConfigContext.js';
import {collectionApi} from '../../../scripts/service/collection-api.js';
import {collectionsContext} from '../../../scripts/context/wishlistContext.js';
import {CollectionsState} from '../../../scripts/context/wishlist-state.js';
import {pushToSnackbar} from '../../../scripts/service/snackbar-service.js';
import {trackShareCollection} from '../../../scripts/service/cube-tracking-layer.js';
import {collectionMenuContext} from '../../../scripts/context/menuContext.js';
import {CollectionMenuState} from '../../../scripts/context/menu-state.js';
import {hideIconFromAssistiveTechnology} from '../icon/icon-accessibility.js';
import {getBackButton} from '../base-layer/base-layer.js';
import baseLayerStyles from '../base-layer/base-layer.scss';
import {BdkTextField} from '@breuni-ui/bdk-text-field';
import {safeDefine} from '@breuni-ui/safe-define';
import {curatedCollectionApi} from '../../../scripts/service/curated-collection-api.js';
import '../tabs/tab-group.js';
import '../tabs/tab-item.js';
import '../tabs/tab-panel.js';
import {repeat} from 'lit/directives/repeat.js';

safeDefine('bdk-ents-text-field', BdkTextField);

@customElement('ents-share-collection-layer')
export class ShareCollectionLayer extends LitElement {
  @consume({context: i18nContext})
  private messages!: Messages;

  @consume({context: wishlistAppConfigContext})
  private wishlistAppConfig!: WishlistAppConfig;

  @consume({context: collectionMenuContext, subscribe: true})
  @property({attribute: false})
  private collectionMenuState!: CollectionMenuState;

  @consume({context: collectionsContext, subscribe: true})
  @property({attribute: false})
  private collectionsState!: CollectionsState;

  static get styles(): CSSResult[] {
    return [css`${unsafeCSS(styles)}`, css`${unsafeCSS(typographyStyles)}`, css`${unsafeCSS(baseLayerStyles)}`];
  }

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    if (changedProperties.has('collectionMenuState')) {
      const oldState = changedProperties.get('collectionMenuState') as CollectionMenuState;
      const wasOpen = oldState?.context?.openShareView ?? false;
      const isOpen = this.collectionMenuState.context.openShareView;

      if (!wasOpen && isOpen) {
        this.focusBocaInput();
      }
    }
  }

  private async focusBocaInput() {
    await this.updateComplete;
    const textField = this.shadowRoot?.querySelector('bdk-ents-text-field[name="boca-id-input"]') as BdkTextField;
    if (textField) {
      textField.focusInput();
    }
  }

  private backButtonAction() {
    if (this.collectionMenuState.context.backButtonCallback) {
      this.collectionMenuState.context.backButtonCallback();
    }
    this.closeLayer();
    this.dispatchEvent(new Event('ents-layer-back', {composed: true, bubbles: true}));
  }

  private closeLayer = () => {
    if (this.collectionMenuState.context.openShareView) {
      this.collectionMenuState.closeShareView();
    }
  };

  private async updateIsSharingAllowed(isSharingAllowed: boolean) {
    const collection = {
      ...this.collectionMenuState.context.collection!,
      isSharingAllowed: isSharingAllowed
    };
    const result = await collectionApi.update(this.wishlistAppConfig.collectionEndpoint, collection);
    if (result.isOk) {
      this.collectionsState.updateOnIsSharingAllowed(
        result.collection!,
        this.collectionMenuState.context.collectionTrackingContext!
      );
      this.collectionMenuState.update({collection: result.collection});
    }
  }

  private async copyLink(
    shareUrl: string,
    collection: WishlistCollection | null,
    collectionTrackingContext: CollectionTrackingContext | null,
    shouldCloseLayer: boolean = true
  ) {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(shareUrl);
      trackShareCollection(collection!, collectionTrackingContext!);
      if (shouldCloseLayer) {
        this.closeLayer();
      }
      pushToSnackbar(this.messages['ents_wishlist_collection_share_snackbar_title']!);
    }
  }

  private async share(
    itemInfoToShare: { url: string, title?: string, text?: string },
    collection: WishlistCollection | null,
    collectionTrackingContext: CollectionTrackingContext | null
  ) {
    if (navigator.share !== undefined && navigator.canShare(itemInfoToShare)) {
      await navigator.share(itemInfoToShare);
      trackShareCollection(collection!, collectionTrackingContext!);
      this.closeLayer();
    }
  }

  private async handleAddBocas(event: Event, collectionId: string) {
    event.preventDefault();
    const form = this.shadowRoot!.querySelector('form')!;
    const formData = new FormData(form);
    const rawInput = formData.get('boca-id-input') as string;
    if (!rawInput) return;
    const uuidPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
    const matches = rawInput.match(uuidPattern);
    if (!matches || matches.length === 0) {
      pushToSnackbar(this.messages['ents_wishlist_collection_share_input_boca_id_added_invalid']!);
      return;
    }
    const newBocaIds = [...new Set(matches)];
    const currentShared = this.collectionMenuState.context.collection?.sharedWithTopCustomers ?? [];
    const updatedCollection = {
      ...this.collectionMenuState.context.collection!,
      sharedWithTopCustomers: [...new Set([...newBocaIds, ...currentShared])],
      isSharingAllowed: true
    };
    const result = await curatedCollectionApi.addTopCustomers(
      this.wishlistAppConfig.curatedCollectionApiToModifyBocaEndpoint,
      collectionId,
      newBocaIds)
    ;
    if (result) {
      this.collectionsState.updateOnSharedWithTopCustomers(updatedCollection);
      this.collectionMenuState.update({collection: updatedCollection});
      const input = form.querySelector('bdk-ents-text-field[name="boca-id-input"]') as BdkTextField;
      if (input) {
        input.value = '';
      }
      const count = newBocaIds.length;
      pushToSnackbar(`${count} ${this.messages['ents_wishlist_collection_share_input_boca_id_added']
        ?.replace('{0}', count > 1 ? 's' : '')}`);
    } else {
      pushToSnackbar(this.messages['ents_wishlist_collection_share_input_boca_id_added_error']!);
    }
  };

  private async handleRemoveBoca(event: Event, collectionId: string, bocaId: string) {
    event.preventDefault();
    const updatedCollection = {
      ...this.collectionMenuState.context.collection!,
      sharedWithTopCustomers: (this.collectionMenuState.context.collection?.sharedWithTopCustomers ?? []).filter(id => id !== bocaId)
    };
    const result = await curatedCollectionApi.removeTopCustomer(
      this.wishlistAppConfig.curatedCollectionApiToModifyBocaEndpoint,
      collectionId,
      bocaId
    );
    if (result) {
      this.collectionsState.updateOnSharedWithTopCustomers(updatedCollection);
      this.collectionMenuState.update({collection: updatedCollection});
      pushToSnackbar(this.messages['ents_wishlist_collection_share_input_boca_id_removed']!);
    } else {
      pushToSnackbar(this.messages['ents_wishlist_collection_share_input_boca_id_removed_error']!);
    }
  }

  private renderShareContent() {
    const collection = this.collectionMenuState.context.collection;
    const collectionTrackingContext = this.collectionMenuState.context.collectionTrackingContext;
    const shareUrl = `${this.wishlistAppConfig.appUrl}${this.wishlistAppConfig.collectionEndpoint}${collection?.id}/`;
    const itemInfoToShare = {
      url: shareUrl,
      title: this.messages['ents_wishlist_sharing_collection_title'],
      text: `${this.messages['ents_wishlist_sharing_collection_body']}`.replace('{}', collection?.name as string),
    };

    const renderShareContent = () => {
      if (navigator.share !== undefined && navigator.canShare(itemInfoToShare)) {
        return html`
          <button
            class="bdk-button" type="button" variant="outline" size="medium"
            @click="${() => this.share(itemInfoToShare, collection, collectionTrackingContext)}"
            data-test-ents-wishlist-collection-share-button
          >
            ${this.messages['ents_wishlist_collection_share_button']}
          </button>
        `;
      }
      if (navigator.clipboard) {
        return html`
          <button
            class="bdk-button" type="button" variant="outline" size="medium"
            @click="${() => this.copyLink(shareUrl, collection, collectionTrackingContext, true)}"
            data-test-ents-wishlist-collection-share-button
            id="copy-shared-list-button"
          >
            ${hideIconFromAssistiveTechnology(copyIcon())}
            ${this.messages['ents_wishlist_collection_share_copy_button']}
          </button>
        `;
      }
      return nothing;
    };

    return html`
      <div>
        <div class="bdk-text-field"
             @click="${() => this.copyLink(shareUrl, collection, collectionTrackingContext, false)}">
          <div class="bdk-text-field--container">
            <input
              class="bdk-text-field--input"
              type="text"
              value="${shareUrl}"
              readonly
              disabled
              data-test-ents-wishlist-collection-share-link
              @click="${(e: Event) => {
                e.stopPropagation();
                this.copyLink(shareUrl, collection, collectionTrackingContext, false);
              }}"
            >
            <div class="bdk-text-field--icons">
              <button type="button"
                      aria-label="${this.messages['ents_wishlist_collection_share_copy_button']}"
                      @click="${(e: Event) => {
                        e.stopPropagation();
                        this.copyLink(shareUrl, collection, collectionTrackingContext, false);
                      }}">
                ${hideIconFromAssistiveTechnology(copyIcon())}
              </button>
            </div>
          </div>
        </div>
        ${renderShareContent()}
      </div>
    `;
  }

  private renderCollectionContent(collection: WishlistCollection) {
    return html`
      ${this.collectionMenuState.context.backButtonCallback == undefined ?
        nothing : getBackButton(this.messages, 'ents-share-collection-layer', this.backButtonAction.bind(this))}
      <div class="ents-share-collection-layer" data-test-ents-wishlist-share-collection-layer>
        <h2 slot="title">${this.messages['ents_wishlist_collection_share_title']}</h2>
        ${this.wishlistAppConfig.isCurator ? this.renderTabs(collection) : this.renderWithoutTabs(collection)}
      </div>
    `;
  }

  private renderWithoutTabs(collection: WishlistCollection) {
    return html`
      <p class="ents-copy">${this.messages['ents_wishlist_collection_share_subtitle']}</p>
      ${this.renderLinkSharingContent(collection)}
      ${(collection?.sharedWithTopCustomers?.length ?? 0) > 0 ? nothing : html`
        <wishlist-alert>
          <p class="ents-copy-small-bold">
            ${this.messages['ents_wishlist_collection_share_hint']}
          </p>
        </wishlist-alert>
      `}
    `;
  }

  private renderTabs(collection: WishlistCollection) {
    return html`
      <ents-tab-group size="small">
        <ents-tab-item data-test-ents-wishlist-collection-share-tab="curator" slot="item" role="tab" size="small">
          ${this.messages['ents_wishlist_collection_share_tab_curated_collection']}
        </ents-tab-item>
        <ents-tab-panel slot="panel" role="tabpanel">
          <form class="ents-share-collection-layer__curator-form"
                @submit="${(e: Event) => this.handleAddBocas(e, collection.id)}">
            <bdk-ents-text-field
              name="boca-id-input"
              type="text"
              maxlength="15000"
            >
              ${this.messages['ents_wishlist_collection_share_input_boca_id_label']}
            </bdk-ents-text-field>
            <button
              class="bdk-button-icon"
              variant="primary"
              size="small"
              type="submit"
            >
              <span role="presentation"> ${hideIconFromAssistiveTechnology(userAddIcon())} </span>
            </button>
          </form>
          <div class="ents-share-collection-layer__curator-badges">
            ${repeat(
              collection.sharedWithTopCustomers ?? [],
              bocaId => html`
                <div class="ents-share-collection-layer__curator-badge bdk-badge-system" variant="neutral">
                  ${bocaId}
                  <button
                    @click="${(e: Event) => this.handleRemoveBoca(e, collection.id, bocaId)}"
                    variant="just-icon"
                    size="small"
                    type="button"
                  >
                    <span role="presentation"> ${hideIconFromAssistiveTechnology(closeIcon())} </span>
                  </button>
                </div>`)
            }
          </div>
        </ents-tab-panel>
        <ents-tab-item data-test-ents-wishlist-collection-share-tab="share" slot="item" role="tab" size="small">
          ${this.messages['ents_wishlist_collection_share_tab_share_via_link']}
        </ents-tab-item>
        <ents-tab-panel slot="panel" role="tabpanel">
          ${this.renderLinkSharingContent(collection)}
        </ents-tab-panel>
      </ents-tab-group>
    `;
  }

  private renderLinkSharingContent(collection: WishlistCollection) {
    return html`
      <div class="ents-share-collection-layer__toggle-wrapper">
        ${(collection?.sharedWithTopCustomers?.length ?? 0) > 0 ? nothing : html`
          <label class="ents-share-collection-layer__edit-share" for="ents-share-collection-layer__toggle">
              <span class="ents-share-collection-layer__switch--label">
                ${collection?.isSharingAllowed ?
                  hideIconFromAssistiveTechnology(sharedIcon()) :
                  hideIconFromAssistiveTechnology(privateIcon())}
                ${this.messages['ents_wishlist_collection_share_label']}
              </span>
            <span class="ents-share-collection-layer__switch" data-test-ents-wishlist-collection-share-switch>
                <input
                  id="ents-share-collection-layer__toggle"
                  type="checkbox"
                  .checked="${collection?.isSharingAllowed}"
                  @click="${() => this.updateIsSharingAllowed(!collection?.isSharingAllowed)}"
                >
                <span class="ents-share-collection-layer__switch--slider">
                  ${hideIconFromAssistiveTechnology(checkmarkIcon())}
                </span>
              </span>
          </label>
        `}
        ${collection?.isSharingAllowed ? this.renderShareContent() : nothing}
      </div>
    `;
  }

  protected render() {
    const collection = this.collectionMenuState.context.collection;
    const showLayer = this.collectionMenuState.context.openShareView && !!collection;
    return html`
      <ents-base-layer
        drawer-test-id="ents-share-collection-layer"
        ?showLayer="${showLayer}"
        @ents-layer-closed="${this.closeLayer}"
      >
        ${collection ? this.renderCollectionContent(collection) : nothing}
      </ents-base-layer>`;
  }
}
